import React, { useEffect, useMemo, useState } from 'react';
import { ApiClient } from '../../api/client';
import { buildCapturedDocument, renderDocumentToCanvas, canvasToPng, canvasToPdf, captureBaseName } from '../../utils/webpageCapture';
import { Globe, X, RefreshCw, Save, AlertTriangle, ExternalLink } from 'lucide-react';
import type { AttachmentFailure } from './AttachmentStatsModal';

// ═══════════════════════════════════════════════════════════════
// „Mentés weboldalként” – ha fájl helyett weboldal érkezett (pl. a képviselő-testület
// névsora egy aloldalon), a kitisztított oldal előnézete és mentése a tételhez:
// képként (PNG), PDF-ként és/vagy HTML-ként. Mentés után a tétel lekerül a hibalistáról.
// ═══════════════════════════════════════════════════════════════

const safeHttpUrl = (u?: string | null) => (u && /^https?:\/\//i.test(u) ? u : null);

interface Props {
  failure: AttachmentFailure;
  onClose: () => void;
  onSaved: (message: string) => void;
}

export const WebpageSaveDialog: React.FC<Props> = ({ failure, onClose, onSaved }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState<{ title: string; html: string; source_url: string; fetched_at: string } | null>(null);
  const [name, setName] = useState(captureBaseName(failure.filename || failure.label));
  const [formats, setFormats] = useState({ png: true, pdf: true, html: false });
  const [saving, setSaving] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    const r: any = await ApiClient.getFailurePage(failure.id);
    if (r?.ok && r.html) {
      setPage({ title: r.title || '', html: r.html, source_url: r.source_url || failure.source_url, fetched_at: r.fetched_at });
      if (!failure.filename && !failure.label && r.title) setName(captureBaseName(r.title));
    } else {
      setError(r?.error || 'A weboldal nem tölthető be');
    }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [failure.id]);

  const docHtml = useMemo(() => (page ? buildCapturedDocument({
    html: page.html,
    title: name || page.title || failure.record_title,
    sourceUrl: page.source_url,
    fetchedAt: page.fetched_at,
  }) : ''), [page, name, failure.record_title]);

  const anyFormat = formats.png || formats.pdf || formats.html;

  const save = async () => {
    if (!page || !anyFormat) return;
    setError(null);
    const base = captureBaseName(name);
    const files: File[] = [];
    let truncated = false;
    try {
      if (formats.png || formats.pdf) {
        setSaving('Kép készítése…');
        const r = await renderDocumentToCanvas(docHtml);
        truncated = r.truncated;
        if (formats.png) files.push(new File([await canvasToPng(r.canvas)], `${base}.png`, { type: 'image/png' }));
        if (formats.pdf) {
          setSaving('PDF készítése…');
          files.push(new File([await canvasToPdf(r.canvas)], `${base}.pdf`, { type: 'application/pdf' }));
        }
      }
      if (formats.html) files.push(new File([docHtml], `${base}.html`, { type: 'text/html' }));

      setSaving('Feltöltés a tételhez…');
      const up: any = await ApiClient.uploadRecordAttachments(failure.record_id, files);
      if (!up?.ok) throw new Error(up?.error || 'A feltöltés nem sikerült');
      const kinds = files.map((f) => f.name.split('.').pop()!.toUpperCase()).join(' + ');
      await ApiClient.resolveAttachmentFailure(failure.id, kinds);
      onSaved(`Mentve a tételhez (${kinds}): „${base}”${truncated ? ' – a nagyon hosszú oldal képe 15 000 képpontnál le lett vágva (a HTML-változat teljes)' : ''}.`);
    } catch (e: any) {
      setError(e?.message || 'A mentés nem sikerült');
    } finally {
      setSaving(null);
    }
  };

  const src = safeHttpUrl(failure.source_url);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-3 sm:p-4 bg-slate-900/60 backdrop-blur-xs" role="dialog" aria-modal="true" aria-labelledby="wp-save-title">
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl w-full max-w-4xl max-h-[92vh] flex flex-col shadow-2xl overflow-hidden">
        <div className="p-4 border-b border-slate-200 dark:border-slate-800 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 id="wp-save-title" className="font-bold text-slate-900 dark:text-white flex items-center gap-2">
              <Globe className="w-4 h-4 text-blue-600" /> Weboldal mentése a tételhez
            </h3>
            <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
              {failure.record_pont} {failure.record_title}
            </p>
            {src && (
              <a href={src} target="_blank" rel="noopener noreferrer nofollow" className="text-[11px] text-blue-600 dark:text-cyan-400 inline-flex items-center gap-1 break-all">
                <ExternalLink className="w-3 h-3 shrink-0" /> {src}
              </a>
            )}
          </div>
          <button type="button" data-modal-close onClick={onClose} disabled={!!saving} aria-label="Bezárás" className="p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {loading ? (
            <div className="p-10 text-center text-xs text-slate-400 flex flex-col items-center gap-2">
              <RefreshCw className="w-6 h-6 animate-spin text-blue-500" /> A weboldal letöltése és kitisztítása…
            </div>
          ) : !page ? (
            <div className="p-4 rounded-xl bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900 text-xs text-rose-800 dark:text-rose-200 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              <div>
                {error}
                <button type="button" onClick={load} className="ml-2 underline font-semibold">Újra</button>
              </div>
            </div>
          ) : (
            <>
              <p className="text-[11px] text-slate-500 dark:text-slate-400">
                Előnézet – a menük, szkriptek és űrlapok nélkül, ahogy a mentett fájlban szerepel. A képek helyén a leírásuk látszik.
              </p>
              <iframe
                title="A mentendő weboldal előnézete"
                sandbox=""
                srcDoc={docHtml}
                className="w-full h-[380px] rounded-xl border border-slate-200 dark:border-slate-700 bg-white"
              />
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="text-xs text-slate-700 dark:text-slate-300 space-y-1">
                  <span className="font-semibold">Fájlnév (kiterjesztés nélkül)</span>
                  <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full px-2.5 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 text-slate-900 dark:text-white"
                  />
                </label>
                <fieldset className="text-xs text-slate-700 dark:text-slate-300">
                  <legend className="font-semibold mb-1">Mentés formátuma (több is választható)</legend>
                  <div className="flex flex-wrap gap-3 pt-1">
                    {([['png', 'Kép (PNG)'], ['pdf', 'PDF'], ['html', 'Weboldal (HTML)']] as const).map(([k, label]) => (
                      <label key={k} className="inline-flex items-center gap-1.5 cursor-pointer">
                        <input type="checkbox" checked={formats[k]} onChange={() => setFormats((f) => ({ ...f, [k]: !f[k] }))} className="w-4 h-4 accent-blue-600" />
                        {label}
                      </label>
                    ))}
                  </div>
                </fieldset>
              </div>
              {error && (
                <div className="p-2.5 rounded-lg bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900 text-xs text-rose-800 dark:text-rose-200">{error}</div>
              )}
            </>
          )}
        </div>

        <div className="p-3.5 border-t border-slate-200 dark:border-slate-800 flex items-center justify-between gap-3 text-xs">
          <span className="text-slate-500 dark:text-slate-400" aria-live="polite">{saving || 'A mentett fájl a tétel mellékletei közé kerül, a hibalistáról pedig lekerül.'}</span>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} disabled={!!saving} className="px-3 py-2 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 font-semibold">Mégse</button>
            <button
              type="button"
              onClick={save}
              disabled={!page || !anyFormat || !!saving || !name.trim()}
              className="px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white font-semibold flex items-center gap-1.5"
            >
              {saving ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              Mentés a tételhez
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WebpageSaveDialog;
